import { initializeApp } from 'firebase/app';
import { getAuth, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { getDatabase } from 'firebase/database';
import { getStorage } from 'firebase/storage';
import { getAnalytics } from 'firebase/analytics';

// Configuração do Firebase - usando variáveis de ambiente
// ⚠️ IMPORTANTE: Configure as variáveis de ambiente no arquivo .env
// Veja .env.example para referência
// 🔒 SEGURANÇA: NUNCA coloque credenciais hardcoded no código fonte

const requiredEnvVars = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Validar que todas as variáveis críticas estão configuradas
const envVarNames: { [key: string]: string } = {
  apiKey: 'REACT_APP_FIREBASE_API_KEY',
  authDomain: 'REACT_APP_FIREBASE_AUTH_DOMAIN',
  databaseURL: 'REACT_APP_FIREBASE_DATABASE_URL',
  projectId: 'REACT_APP_FIREBASE_PROJECT_ID',
  storageBucket: 'REACT_APP_FIREBASE_STORAGE_BUCKET',
  messagingSenderId: 'REACT_APP_FIREBASE_MESSAGING_SENDER_ID',
  appId: 'REACT_APP_FIREBASE_APP_ID',
  measurementId: 'REACT_APP_FIREBASE_MEASUREMENT_ID'
};

const missingVars = Object.entries(requiredEnvVars)
  .filter(([key, value]) => !value && key !== 'measurementId') // measurementId é opcional
  .map(([key]) => envVarNames[key]);

if (missingVars.length > 0) {
  const isDevelopment = process.env.NODE_ENV === 'development';
  const isBuildTime = typeof window === 'undefined'; // Durante o build do webpack
  
  if (isDevelopment) {
    console.warn('⚠️ AVISO: As seguintes variáveis de ambiente do Firebase não estão configuradas:');
    console.warn(missingVars.join(', '));
    console.warn('Crie um arquivo .env na raiz do projeto com essas variáveis.');
    console.warn('Veja .env.example para referência.');
  } else if (isBuildTime) {
    // Durante o build, não permitir que compile sem as variáveis
    console.error('❌ ERRO CRÍTICO: Variáveis de ambiente do Firebase não configuradas durante o build!');
    console.error('Variáveis faltando:', missingVars.join(', '));
    throw new Error(
      `Configuração do Firebase incompleta. Variáveis faltando: ${missingVars.join(', ')}. ` +
      'Configure as variáveis de ambiente antes de fazer deploy em produção.'
    );
  } else {
    // Em runtime (navegador), se as variáveis não estão no código compilado,
    // significa que o build foi feito sem elas - isso é um erro crítico
    console.error('❌ ERRO CRÍTICO: Variáveis de ambiente do Firebase não encontradas no código compilado!');
    console.error('Variáveis faltando:', missingVars.join(', '));
    console.error('Isso significa que o build foi feito sem as variáveis configuradas.');
    console.error('Configure as variáveis de ambiente no Azure DevOps e faça um novo build.');
    throw new Error(
      `Configuração do Firebase incompleta. O build foi feito sem as variáveis necessárias. ` +
      `Variáveis faltando: ${missingVars.join(', ')}. ` +
      'Configure as variáveis de ambiente no Azure DevOps Variable Group e faça um novo deploy.'
    );
  }
}

const firebaseConfig = {
  apiKey: requiredEnvVars.apiKey!,
  authDomain: requiredEnvVars.authDomain!,
  databaseURL: requiredEnvVars.databaseURL!,
  projectId: requiredEnvVars.projectId!,
  storageBucket: requiredEnvVars.storageBucket!,
  messagingSenderId: requiredEnvVars.messagingSenderId!,
  appId: requiredEnvVars.appId!,
  measurementId: requiredEnvVars.measurementId
};

// Inicializar Firebase
const app = initializeApp(firebaseConfig);

// Inicializar serviços
export const auth = getAuth(app);
export const database = getDatabase(app);
export const storage = getStorage(app);
export const analytics = getAnalytics(app);

// Configurar persistência da sessão para durar mais tempo
setPersistence(auth, browserLocalPersistence).catch((error) => {
  console.error('Erro ao configurar persistência:', error);
});

export default app;
